export type Locale = 'es' | 'en';

const es = {
  meta: {
    title: 'reciboofertas · El mercado al revés',
    description: 'Publica lo que buscas y recibe ofertas de vendedores cercanos. Muy pronto en CDMX.',
    ogLocale: 'es_MX',
  },
  a11y: {
    skip: 'Saltar al contenido',
    nav: 'Navegación principal',
    openMenu: 'Abrir menú',
    closeMenu: 'Cerrar menú',
    language: 'Cambiar idioma',
    logo: 'reciboofertas, inicio',
    scroll: 'Desplázate para descubrir cómo funciona',
  },
  nav: {
    how: 'Cómo funciona', buyers: 'Compradores', sellers: 'Vendedores', app: 'La app', cta: 'Quiero probarla',
  },
  hero: {
    eyebrow: 'EL MERCADO AL REVÉS',
    headlineA: 'Tú pides.',
    headlineB: 'Ellos ofrecen.',
    headlineC: 'Tú eliges.',
    description: 'Tú dices qué necesitas. Vendedores te hacen propuestas y tú decides con cuál quedarte.',
    primary: 'Quiero probar reciboofertas',
    secondary: 'Ver cómo funciona',
    launch: 'Muy pronto en CDMX',
    requestLabel: 'BUSCO',
    requestTitle: 'Bicicleta urbana',
    requestBudget: 'Presupuesto · $9,000 MXN',
    requestArea: 'Roma Norte · 5 km',
    offersLabel: 'OFERTAS RECIBIDAS',
    requestStage: 'TÚ PIDES',
    offersStage: 'ELLOS OFRECEN',
    choiceStage: 'TÚ ELIGES',
    best: 'Mejor opción',
    offerOne: '$7,800',
    offerTwo: '$8,400',
    offerThree: '$8,900',
  },
  problem: {
    eyebrow: 'MENOS BÚSQUEDA. MÁS OPCIONES.',
    headline: 'Deja de buscar. Haz que las ofertas lleguen a ti.',
    description: 'No más pestañas infinitas, publicaciones vencidas ni la misma pregunta enviada diez veces.',
    chaos: ['¿Sigue disponible?', 'Publicado hace 8 meses', 'Sin respuesta', 'Precio incompleto', 'Demasiado lejos'],
    clarity: 'Una solicitud clara',
    relevant: 'Ofertas relevantes',
    example: { title: 'Reloj automático', meta: 'Acero · $6,500 MXN' },
  },
  how: {
    eyebrow: 'ASÍ DE SIMPLE',
    headline: 'Una necesidad. Varias propuestas. Tú decides.',
    description: 'El proceso gira alrededor de lo que tú quieres comprar, no de un catálogo interminable.',
    steps: [
      { number: '01', title: 'Publica lo que buscas', body: 'Elige una categoría, describe lo que necesitas, agrega los detalles importantes y define tu zona.', caption: 'Crea una solicitud con presupuesto y preferencias.' },
      { number: '02', title: 'Recibe ofertas cercanas', body: 'Vendedores que pueden ayudarte responden con propuestas hechas para tu solicitud.', caption: 'Compara propuestas en un solo lugar.' },
      { number: '03', title: 'Compara y elige', body: 'Revisa precios, detalles, fotos y conversaciones. La decisión final siempre es tuya.', caption: 'Pregunta, compara y elige con calma.' },
    ],
  },
  showcase: {
    eyebrow: 'LA EXPERIENCIA',
    headline: 'Todo lo que necesitas para decidir.',
    description: 'Solicitudes, ofertas y conversaciones viven juntas para que comparar sea mucho más fácil.',
    items: [
      { title: 'Publica en minutos', body: 'Los detalles cambian según la categoría para pedir justo la información que importa.' },
      { title: 'Ofertas comparables', body: 'Precio, condición y propuesta se presentan de forma clara, una junto a otra.' },
      { title: 'Habla antes de elegir', body: 'Resuelve dudas directamente con cada vendedor sin perder el contexto de la oferta.' },
    ],
    screenAlts: {
      request: 'Pantalla de reciboofertas para crear una solicitud de compra',
      offers: 'Pantalla de reciboofertas con tres ofertas recibidas',
      chat: 'Pantalla de conversación entre comprador y vendedor',
      feed: 'Pantalla de solicitudes de compradores cercanos',
      detail: 'Pantalla con el detalle de una solicitud y sus ofertas',
    },
  },
  live: {
    eyebrow: 'FOTOS EN VIVO',
    headline: '¿Quieres verlo como está hoy?',
    accent: 'Pide una foto tomada al momento.',
    description: 'La app está diseñada para permitir que el comprador solicite una foto capturada desde la cámara en ese momento. Así tienes información más actual antes de decidir.',
    note: 'Una foto en vivo aporta contexto actual; no garantiza por sí sola la condición del producto.',
    capture: 'Captura desde la app',
    timestamp: 'Marcada como tomada al momento',
    cameraAlt: 'Cámara de reciboofertas para tomar una foto en vivo',
    resultAlt: 'Pantalla de oferta que solicita agregar una foto en vivo',
  },
  audiences: {
    eyebrow: 'DOS LADOS. UNA MEJOR CONVERSACIÓN.',
    headline: 'Más útil para quien compra. Más relevante para quien vende.',
    buyers: {
      label: 'PARA COMPRADORES', title: 'Pide una vez. Compara en un solo lugar.',
      items: ['Evita búsquedas interminables', 'Recibe propuestas para una necesidad real', 'Compara precio, detalles y fotos', 'Pregunta antes de decidir', 'Conserva el control de la elección'],
    },
    sellers: {
      label: 'PARA VENDEDORES', title: 'Encuentra intención real cerca de ti.',
      items: ['Descubre solicitudes relevantes', 'Responde solo cuando puedes ayudar', 'Adapta cada propuesta', 'Conversa con personas interesadas', 'Depende menos de listados pasivos'],
    },
    buyerPreview: 'Lentes polarizados',
    sellerPreview: { badge: 'NUEVA SOLICITUD', title: 'Auto compacto 2021', meta: 'Presupuesto · $185,000 MXN', action: 'Preparar oferta' },
  },
  city: {
    eyebrow: 'PRIMERA PARADA',
    headline: 'Empezamos en CDMX.',
    description: 'La experiencia inicial conectará solicitudes de compra con vendedores relevantes en zonas cercanas de la Ciudad de México.',
    labels: ['Centro', 'Roma', 'Coyoacán', 'Polanco', 'Del Valle'],
    badge: 'Lanzamiento inicial',
  },
  waitlist: {
    eyebrow: 'SÉ DE LOS PRIMEROS',
    headline: 'Tú publica. Deja que las ofertas lleguen.',
    description: '¿Quieres probar reciboofertas o vender desde el comienzo? Escríbenos y cuéntanos cómo te gustaría usarlo.',
    contactTitle: 'Hablemos.',
    contactBody: 'Envíanos un correo y te contactaremos con los próximos pasos para el lanzamiento en CDMX.',
    contactCta: 'Escribir a hello@reciboofertas.com',
    contactSubject: 'Quiero conocer reciboofertas',
  },
  footer: {
    tagline: 'Tú pides. Ellos ofrecen.',
    location: 'Hecho para empezar en CDMX.',
    rights: 'reciboofertas · 2026',
    privacy: 'Privacidad', terms: 'Términos',
  },
} as const;

type DeepWiden<T> = T extends string
  ? string
  : T extends readonly (infer U)[]
    ? readonly DeepWiden<U>[]
    : T extends object
      ? { readonly [K in keyof T]: DeepWiden<T[K]> }
      : T;

export type Copy = DeepWiden<typeof es>;

const en: Copy = {
  meta: {
    title: 'reciboofertas · The marketplace in reverse',
    description: 'Post what you need and receive offers from nearby sellers. Coming soon to Mexico City.',
    ogLocale: 'en_US',
  },
  a11y: {
    skip: 'Skip to content', nav: 'Primary navigation', openMenu: 'Open menu', closeMenu: 'Close menu', language: 'Change language', logo: 'reciboofertas, home', scroll: 'Scroll to discover how it works',
  },
  nav: { how: 'How it works', buyers: 'Buyers', sellers: 'Sellers', app: 'The app', cta: 'Get early access' },
  hero: {
    eyebrow: 'THE MARKETPLACE IN REVERSE',
    headlineA: 'You ask.', headlineB: 'They offer.', headlineC: 'You choose.',
    description: 'Tell us what you need. Nearby sellers send their offers, and you decide which one works best for you.',
    primary: 'Get early access', secondary: 'See how it works', launch: 'Coming soon to Mexico City',
    requestLabel: 'LOOKING FOR', requestTitle: 'City bicycle', requestBudget: 'Budget · $9,000 MXN', requestArea: 'Roma Norte · 5 km',
    offersLabel: 'OFFERS RECEIVED', requestStage: 'YOU ASK', offersStage: 'THEY OFFER', choiceStage: 'YOU CHOOSE', best: 'Best option', offerOne: '$7,800', offerTwo: '$8,400', offerThree: '$8,900',
  },
  problem: {
    eyebrow: 'LESS SEARCHING. MORE OPTIONS.', headline: 'Stop searching. Let the offers come to you.',
    description: 'No more endless tabs, stale listings, or sending the same question ten times.',
    chaos: ['Still available?', 'Posted 8 months ago', 'No response', 'Incomplete price', 'Too far away'], clarity: 'One clear request', relevant: 'Relevant offers', example: { title: 'Automatic watch', meta: 'Steel · MXN $6,500' },
  },
  how: {
    eyebrow: 'THAT SIMPLE', headline: 'One need. Several proposals. You decide.', description: 'The process revolves around what you want to buy—not an endless catalog.',
    steps: [
      { number: '01', title: 'Post what you need', body: 'Choose a category, describe what you need, add the important details, and define your area.', caption: 'Create a request with your budget and preferences.' },
      { number: '02', title: 'Receive nearby offers', body: 'Sellers who can help respond with proposals tailored to your request.', caption: 'Compare proposals in one place.' },
      { number: '03', title: 'Compare and choose', body: 'Review prices, details, photos, and conversations. The final decision is always yours.', caption: 'Ask, compare, and choose at your pace.' },
    ],
  },
  showcase: {
    eyebrow: 'THE EXPERIENCE', headline: 'Everything you need to decide.', description: 'Requests, offers, and conversations stay together, making comparison much easier.',
    items: [
      { title: 'Post in minutes', body: 'Details adapt to each category, so you only provide information that matters.' },
      { title: 'Comparable offers', body: 'Price, condition, and proposal appear clearly, side by side.' },
      { title: 'Talk before choosing', body: 'Resolve questions with each seller without losing the context of their offer.' },
    ],
    screenAlts: {
      request: 'reciboofertas screen for creating a purchase request', offers: 'reciboofertas screen showing three received offers', chat: 'Conversation between a buyer and seller', feed: 'Nearby buyer request feed', detail: 'Request details and received offers',
    },
  },
  live: {
    eyebrow: 'LIVE PHOTOS', headline: 'Want to see what it looks like today?', accent: 'Request a photo taken right now.',
    description: 'The app is designed to let buyers request a photo captured with the in-app camera at that moment. It gives you more current information before deciding.',
    note: 'A live photo provides current context; by itself, it does not guarantee an item’s condition.',
    capture: 'Captured in the app', timestamp: 'Marked as taken at that moment', cameraAlt: 'reciboofertas camera for taking a live photo', resultAlt: 'Offer screen asking the seller to add a live photo',
  },
  audiences: {
    eyebrow: 'TWO SIDES. A BETTER CONVERSATION.', headline: 'More useful for buyers. More relevant for sellers.',
    buyers: { label: 'FOR BUYERS', title: 'Ask once. Compare in one place.', items: ['Avoid endless searching', 'Receive offers for a real need', 'Compare price, details, and photos', 'Ask before deciding', 'Stay in control of the choice'] },
    sellers: { label: 'FOR SELLERS', title: 'Find real intent near you.', items: ['Discover relevant requests', 'Respond only when you can help', 'Tailor every proposal', 'Talk with interested buyers', 'Rely less on passive listings'] },
    buyerPreview: 'Polarized sunglasses',
    sellerPreview: { badge: 'NEW REQUEST', title: '2021 compact car', meta: 'Budget · MXN $185,000', action: 'Prepare offer' },
  },
  city: {
    eyebrow: 'FIRST STOP', headline: 'Starting in Mexico City.', description: 'The initial experience will connect purchase requests with relevant sellers in nearby areas of Mexico City.', labels: ['Centro', 'Roma', 'Coyoacán', 'Polanco', 'Del Valle'], badge: 'Initial launch',
  },
  waitlist: {
    eyebrow: 'BE AMONG THE FIRST', headline: 'Post what you need. Let the offers come to you.', description: 'Want to try reciboofertas or start selling from day one? Email us and tell us how you would like to use it.',
    contactTitle: 'Let’s talk.', contactBody: 'Send us an email and we’ll get back to you with the next steps for our Mexico City launch.', contactCta: 'Email hello@reciboofertas.com', contactSubject: 'I want to learn about reciboofertas',
  },
  footer: { tagline: 'You ask. They offer.', location: 'Made to launch in Mexico City.', rights: 'reciboofertas · 2026', privacy: 'Privacy', terms: 'Terms' },
};

export const translations: Record<Locale, Copy> = { es, en };
